/**
 * VulnerabilityDrawerHeader Component
 *
 * Displays the vulnerability header section with a two-row layout:
 * - Row 1: Full-width vulnerability name with share button
 * - Row 2: Badges (left) | Status boxes (right)
 *
 * Pure presentation component - mirrors AssetDrawerHeader layout pattern.
 */

import { InformationCircleIcon } from '@heroicons/react/24/outline';

import { ShareIconButton } from '@/components/actions';
import { DashedTag, RiskTag } from '@/components/chariot-ui';
import { ChipsList } from '@/components/ChipsList';
import { CopyToClipboard } from '@/components/CopyToClipboard';
import { DateStatusBox } from '@/components/DateStatusBox';
import { OverflowText } from '@/components/OverflowText';
import { ScanStatus } from '@/components/ScanStatusContainer';
import { Tooltip } from '@/components/Tooltip';
import { VulnerabilityStatusComponent } from '@/sections/vulnerabilities/components/VulnerabilityStatus';
import { Risk, RiskStatus } from '@/types';
import { formatAttackSurfaceLabel } from '@/utils/mappings.util';

import { getRiskTagSeverity } from './VulnerabilityDrawer';

// Helper type for ML prediction data from history
export interface MLPrediction {
  recommendation: string;
  logit: number;
  model: string;
  timestamp: string;
}

interface MLPredictionBadgeProps {
  prediction: MLPrediction;
}

function MLPredictionBadge({ prediction }: MLPredictionBadgeProps) {
  const { recommendation, logit } = prediction;

  let label: string;
  let colorClass: string;

  if (recommendation === 'D') {
    label = 'AI: Likely False Positive';
    colorClass = 'text-red-500 dark:text-red-400';
  } else if (recommendation === 'O') {
    label = 'AI: Likely True Positive';
    colorClass = 'text-green-600 dark:text-green-400';
  } else {
    label = 'AI: Needs Triage';
    colorClass = 'text-chariot-text-secondary';
  }

  const tooltipTitle = [
    'AI model threshold logic:',
    '• Score < -6.5 → Likely False Positive',
    '• Score > 6.5 → Likely True Positive',
    '• Otherwise → Needs Triage',
    '',
    `This finding's score: ${logit.toFixed(2)}`,
  ].join('\n');

  return (
    <Tooltip title={tooltipTitle}>
      <span
        className={`flex items-center gap-1 text-xs font-medium ${colorClass}`}
      >
        {label}
        <InformationCircleIcon className="size-3.5 shrink-0" />
      </span>
    </Tooltip>
  );
}

export interface VulnerabilityDrawerHeaderProps {
  risk: Risk;
  existingWriteup?: { title?: string };
  vulnerabilityTitle?: string;
  mlPrediction?: MLPrediction;
  isSSO: boolean;
  ssoTags: string[];
}

/**
 * VulnerabilityDrawerHeader renders the top section of the vulnerability drawer with:
 * - Row 1: Vulnerability name with copy functionality and share button
 * - Row 2: Left side (severity badge, ML prediction, SSO indicator) | Right side (status boxes)
 */
export function VulnerabilityDrawerHeader({
  risk,
  existingWriteup,
  vulnerabilityTitle,
  mlPrediction,
  isSSO,
  ssoTags,
}: VulnerabilityDrawerHeaderProps) {
  return (
    <div
      className="flex w-full flex-col gap-4"
      data-testid="vulnerability-drawer-header"
    >
      {/* Row 1: Full-width vulnerability name with share button */}
      <div className="flex items-center gap-2">
        <CopyToClipboard
          alwaysShowCopyIcon
          className="min-w-0 flex-1 gap-2 overflow-hidden [&_svg]:ml-0"
        >
          <OverflowText
            text={
              risk.title ||
              existingWriteup?.title ||
              vulnerabilityTitle ||
              risk.name
            }
            className="text-chariot-text-primary overflow-hidden text-xl font-bold whitespace-nowrap"
          />
        </CopyToClipboard>
        <ShareIconButton data-testid="share-vulnerability" />
      </div>

      {/* GUID display */}
      {risk.guid && (
        <div className="flex items-center gap-1.5">
          <span className="text-chariot-text-secondary text-xs">ID:</span>
          <CopyToClipboard textToCopy={risk.guid} className="gap-1">
            <Tooltip title={risk.guid}>
              <span className="text-chariot-text-secondary font-mono text-xs">
                {risk.guid.substring(
                  0,
                  risk.guid.indexOf('-') > 0 ? risk.guid.indexOf('-') : 8
                )}
              </span>
            </Tooltip>
          </CopyToClipboard>
        </div>
      )}
      {/* Row 2: Badges (left) | Status boxes (right) */}
      <div className="flex items-start justify-between gap-6">
        {/* Left side: severity badge, ML prediction, SSO indicator */}
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex items-center gap-2">
            <RiskTag severity={getRiskTagSeverity(risk.status)} size="xs" />
            {risk.attackSurface?.length ? (
              <ChipsList
                items={risk.attackSurface.map(formatAttackSurfaceLabel)}
                variant="default"
                size="xs"
                maxVisible={Infinity}
                data-testid="vulnerability-attack-surface-chip"
              />
            ) : (
              <DashedTag label="No Attack Surface" size="xs" />
            )}
          </div>
          {mlPrediction && risk.status[0] === RiskStatus.Triaged && (
            <div className="flex items-center gap-2">
              <MLPredictionBadge prediction={mlPrediction} />
            </div>
          )}
          {isSSO && (
            <div className="flex items-center gap-2">
              <Tooltip title={ssoTags.join(', ')}>
                <div>
                  <DashedTag label="SSO identified" size="xs" />
                </div>
              </Tooltip>
            </div>
          )}
        </div>

        {/* Right side: status boxes */}
        <div className="flex shrink-0 items-start gap-2">
          <ScanStatus risk={risk} />
          {
            <div className="bg-chariot-background-secondary flex h-[88px] min-w-[153px] shrink-0 flex-col items-center justify-center gap-1 rounded-md px-4">
              <p className="text-chariot-text-primary text-xs font-semibold">
                Vulnerability Status
              </p>
              <VulnerabilityStatusComponent vulnerability={risk} />
            </div>
          }
          <DateStatusBox firstSeen={risk.created} lastSeen={risk.visited} />
        </div>
      </div>
    </div>
  );
}
